import { handleError } from 'common/utils/util.errors';
import {
  addCompsToCCReport,
  deleteCCReport,
  getCostPerDivision,
  addOrUpdateNoteToReport,
  deleteNoteFromReport,
  getNotes,
  exportCCReportAsWord,
  duplicateCCReport,
} from './construction-cost-report.api';
import React, { useRef } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { LinearProgress, Tab, Tabs } from '@mui/material';
import { joinDateAndUser } from 'common/utils/common.util';
import keys from 'common/react-query/data-keys';
import { toast } from 'react-toastify';
import { ROUTES } from 'common/constants/urls';
import { usePrompt } from '../AppPrompt';
import {
  captureScatterPlot,
  extractMetrics,
  generateStaticMapUrl,
  getComparablesLocations,
  getFiltersObject,
  getProjectTypesString,
} from './construction-cost-report.util';
import CCReportSummary from './CCReportSummary';
import CCReportBudgetInfo from './CCReportCostInfo';
import CCReportBudgetPerDivision from './CCReportCostPerDivision';
import { CCReport, CCReportComp, CCReportExportData } from './construction-cost-report.type';
import { Grid } from '@mui/material';
import CCReportInputsCard from './CCReportInputsCard';
import { getDivisions } from '../../components/glossary/glossary.api';
import ProjectCompsDialog from '../../components/project-comps/ProjectCompsDialog';
import { GridRowId } from '@mui/x-data-grid';
import CCReportFooter from './CCReportFooter';
import { NotesAccordion } from '../general/Notes/NotesAccordion';
import { NoteDTO } from 'common/utils/util.type';
import { ReportTypes } from 'common/utils/util.type';
import CCComparablesDetails from './CCComparablesDetails';
import { prepareExportData } from './ExportCCReport/PrepareExportData.util';
import CCReportProjectInfo from './CCReportProjectInfo';
import CCExportDialog from './CCExportDialog';
import GenericCopyReportDialog from '../general/GenericCopyReportDialog';

const DEFAULT_CC_EXPORT_PREFERENCES = {
  includeGSF: true,
  includeNSF: true,
  includeUnits: true,
} as const;

interface CCReportRendererProps {
  report: CCReport;
  updateReport: (updatedReport: CCReport) => void;
  isReportDirty: boolean;
  isReportLoading: boolean;
  saveReport: () => void;
  undoChanges: () => void;
  refetchReport: () => void;
}

const CCReportRenderer: React.FC<CCReportRendererProps> = ({
  report,
  updateReport,
  isReportDirty,
  isReportLoading,
  saveReport,
  undoChanges,
  refetchReport,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getConfirmation } = usePrompt();
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [tabValue, setTabValue] = React.useState(0);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [isCompsDialogOpen, setIsCompsDialogOpen] = React.useState(false);
  const [isAddingComps, setIsAddingComps] = React.useState(false);
  const [addCompFilterString, setAddCompFilterString] = React.useState(report.filtersString);
  const [notes, setNotes] = React.useState<NoteDTO[]>([]);
  const [isEditMode, setIsEditMode] = React.useState(false);
  const [isExporting, setIsExporting] = React.useState(false);
  const [exportReportDialogOpen, setIsExportReportDialogOpen] = React.useState(false);
  const [exportPreferences, setExportPreferences] = React.useState<Partial<CCReportExportData>>(DEFAULT_CC_EXPORT_PREFERENCES);
  const [includeSoftCost, setIncludeSoftCost] = React.useState(report.includeSoftCost); // Toggle state
  const [openCopyReportDialog, setOpenCopyReportDialog] = React.useState(false);

  const scatterRefGSF = useRef(null);
  const scatterRefUnit = useRef(null);
  const scatterRefNSF = useRef(null);

  const openExportDialog = () => setIsExportReportDialogOpen(true);
  const closeExportDialog = () => {
    setExportPreferences(DEFAULT_CC_EXPORT_PREFERENCES);
    setIsExportReportDialogOpen(false);
  };

  const handleCreateCopy = () => {
    setOpenCopyReportDialog(true);
  };
  const handleCloseDialog = () => {
    setOpenCopyReportDialog(false);
  };

  const toggleEditMode = () => setIsEditMode(val => !val);
  // reset if props change
  React.useEffect(() => {
    if (isEditMode) setIsEditMode(false);
  }, [report]);

  const updateComps = React.useCallback(
    (updatedComps: CCReportComp[]) => {
      const updatedReport = { ...report, comparableProjects: updatedComps };
      updateReport(updatedReport);
    },
    [report, updateReport],
  );

  const open = Boolean(anchorEl);
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  const toggleIncludeSoftCost = () => {
    const updatedReport = { ...report, includeSoftCost: !includeSoftCost };
    setIncludeSoftCost(!includeSoftCost);
    updateReport(updatedReport);
  };

  const handleExportInvoice = React.useCallback(() => {
    window.open(`${ROUTES.CC_REPORTS}/${report.id}/invoice`, '_blank');
  }, [report.id]);

  // Fetching list of divisions
  const {
    data: divisions,
    isLoading: isLoadingDivisions,
    isError: isErrorDivisions,
    error: errorDivisions,
  } = useQuery(keys.divisions, getDivisions);

  // Fetching cost per division
  const {
    data: costPerDivision,
    isLoading: isLoadingReport,
    isError: isErrorReport,
    error: errorReport,
    refetch: refetchCostPerDivision,
  } = useQuery([report.id], () => getCostPerDivision(report.id), {
    enabled: !!report.id,
  });

  if (isLoadingDivisions || isLoadingReport) {
    return <LinearProgress />;
  }

  if (isErrorDivisions || isErrorReport) {
    console.error(errorDivisions || errorReport);
    navigate(ROUTES.NOT_FOUND);
    return null;
  }

  if (!divisions || !costPerDivision) {
    navigate(ROUTES.NOT_FOUND);
    return null;
  }

  const handleDelete = async () => {
    try {
      if (!report) return;
      const isConfirmed = await getConfirmation({
        heading: 'Delete Report',
        description: `Are you sure you want to delete this report: ${report.name}?`,
        confirmButton: {
          label: 'Delete',
          color: 'error',
        },
        cancelButton: {
          label: 'Cancel',
          color: 'secondary',
        },
      });
      if (!isConfirmed) return;
      setIsDeleting(true);
      await deleteCCReport(report.id);
      toast.success('Report deleted successfully');
      queryClient.invalidateQueries(keys.ccReports);
      navigate(ROUTES.CC_REPORTS);
    } catch (error) {
      handleError(error);
    } finally {
      setIsDeleting(false);
    }
  };
  const addComps = async (projects: readonly GridRowId[], filterString?: string) => {
    if (projects.length === 0) {
      toast.error('Please select at least one project to add.');
      return;
    }
    setIsAddingComps(true);
    try {
      if (isReportDirty) {
        const isConfirmed = await getConfirmation({
          description: 'Adding these comps will overwrite any unsaved changes in the Report. Are you sure you want to continue?',
          confirmButton: {
            label: 'Continue',
            color: 'error',
          },
          cancelButton: {
            label: 'Cancel',
            color: 'secondary',
          },
        });
        if (!isConfirmed) return;
      }
      // Extract project IDs from selectedProjectComps
      const selectedProjectIds = projects;

      // Combine both lists of project IDs (avoid duplicates if necessary)
      const combinedProjectIds = Array.from(new Set([...selectedProjectIds]));

      const response = await addCompsToCCReport({
        ccReportId: report.id,
        projectIds: combinedProjectIds,
        filterString: filterString,
      });
      queryClient.invalidateQueries([keys.ccReport, report.id]);
      refetchCostPerDivision();
      refetchReport();
      toast.success('Comps Added successfully!');
      handleCloseCompsDialog();
    } catch (error) {
      handleError(error);
    } finally {
      setIsAddingComps(false);
    }
  };

  const handleOpenCompsDialog = () => {
    setIsCompsDialogOpen(true);
  };
  const handleCloseCompsDialog = () => {
    setIsCompsDialogOpen(false);
  };
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const {
    minGSF,
    maxGSF,
    minUnitCount,
    maxUnitCount,
    minSiteArea,
    maxSiteArea,
    minStoryCount,
    maxStoryCount,
    minBuildingCount,
    maxBuildingCount,
    minBudget,
    maxBudget,
    minBudgetPerGSF,
    maxBudgetPerGSF,
    avgBudgetPerGSF,
    minBudgetPerNSF,
    maxBudgetPerNSF,
    avgBudgetPerNSF,
    minBudgetPerUnit,
    maxBudgetPerUnit,
    avgBudgetPerUnit,
    minPeriodEnding,
    maxPeriodEnding,
  } = extractMetrics(report, includeSoftCost);

  const projectTypesString = getProjectTypesString(report);
  const locations = getComparablesLocations(report);
  const filtersObject = getFiltersObject(report);

  const handleExport = async () => {
    setIsExporting(true);
    closeExportDialog();

    // Generate the static map URL
    const mapImageUrl = generateStaticMapUrl(report);

    try {
      const scatterGSFBase64 = await captureScatterPlot(scatterRefGSF);
      const scatterUnitBase64 = await captureScatterPlot(scatterRefUnit);
      const scatterNSFBase64 = await captureScatterPlot(scatterRefNSF);
      // Fetch the map image as a blob
      let mapImageBase64 = null;

      if (mapImageUrl) {
        const mapImageBlob = await fetch(mapImageUrl).then(res => res.blob());

        // Convert the blob to a Base64 string
        mapImageBase64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(mapImageBlob);
        });
      }

      // Prepare export data, including the map image as Base64
      const exportData = {
        ...prepareExportData(
          report,
          locations,
          costPerDivision,
          divisions,
          minGSF,
          maxGSF,
          minSiteArea,
          maxSiteArea,
          minUnitCount,
          maxUnitCount,
          minBuildingCount,
          maxBuildingCount,
          minStoryCount,
          maxStoryCount,
          minBudget,
          maxBudget,
          avgBudgetPerGSF,
          minBudgetPerGSF,
          maxBudgetPerGSF,
          minBudgetPerNSF,
          maxBudgetPerNSF,
          avgBudgetPerNSF,
          minBudgetPerUnit,
          maxBudgetPerUnit,
          avgBudgetPerUnit,
          minPeriodEnding,
          maxPeriodEnding,
          includeSoftCost,
        ),
        ...exportPreferences,
        MapImage: mapImageBase64,
        ScatterPlotGSF: scatterGSFBase64 || undefined,
        ScatterPlotUnit: scatterUnitBase64 || undefined,
        ScatterPlotNSF: scatterNSFBase64 || undefined,
      };

      const response = await exportCCReportAsWord(exportData);
      const url = window.URL.createObjectURL(new Blob([response]));

      const reportName = exportData.ReportName || 'Construction_Cost_Report';
      const exportDate = new Date().toLocaleDateString();
      const fileName = `${reportName}_${exportDate}.docx`;

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Report exported successfully.');
    } catch (error) {
      toast.error('Error exporting report.');
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleCreateCopyCC = async (reportName: string) => {
    try {
      const response = await duplicateCCReport(report.id, reportName);
      toast.success('Report Duplicated Successfully');
      navigate(`${ROUTES.CC_REPORTS}/${response.id}`);
    } catch (error: any) {
      toast.error(error.message || error.response?.data?.message || 'Error duplicating report.');
    }
  };

  return (
    <>
      <Grid container spacing={2} p={1}>
        <Grid size={{ xs: 12 }}>
          <CCReportSummary
            report={report}
            typeOfReport={ReportTypes.CC}
            updateReport={updateReport}
            filtersObject={filtersObject}
            minGSF={minGSF}
            maxGSF={maxGSF}
            projectTypesString={projectTypesString}
            minBudget={minBudget}
            maxBudget={maxBudget}
            locations={locations}
            minUnitCount={minUnitCount}
            maxUnitCount={maxUnitCount}
            minStoryCount={minStoryCount}
            maxStoryCount={maxStoryCount}
            minPeriodEnding={minPeriodEnding}
            maxPeriodEnding={maxPeriodEnding}
            minBuildingCount={minBuildingCount}
            maxBuildingCount={maxBuildingCount}
            minSiteArea={minSiteArea}
            maxSiteArea={maxSiteArea}
            isEditMode={isEditMode}
            setIsEditMode={setIsEditMode}
            toggleEditMode={toggleEditMode}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <CCReportProjectInfo report={report} updateReport={updateReport} />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <CCComparablesDetails
            report={report}
            isReportDirty={isReportDirty}
            updateComps={updateComps}
            handleOpenCompsDialog={handleOpenCompsDialog}
            typeOfReport={ReportTypes.CC}
            includeSoftCost={includeSoftCost}
            toggleIncludeSoftCost={toggleIncludeSoftCost}
          />
        </Grid>
      </Grid>

      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        sx={{
          marginBottom: 1,
          '.MuiTab-root': {
            '&:hover': {
              backgroundColor: '#eaf5f7',
            },
            fontWeight: 'bold',
          },
        }}
      >
        <Tab label='Average Cost' />
        <Tab label='Projected Cost' />
      </Tabs>
      {tabValue === 0 && (
        <>
          <CCReportBudgetInfo
            report={report}
            displayProjections={false}
            maxBudgetPerGSF={maxBudgetPerGSF}
            avgBudgetPerGSF={avgBudgetPerGSF}
            minBudgetPerGSF={minBudgetPerGSF}
            minBudgetPerNSF={minBudgetPerNSF}
            maxBudgetPerNSF={maxBudgetPerNSF}
            avgBudgetPerNSF={avgBudgetPerNSF}
            minBudgetPerUnit={minBudgetPerUnit}
            maxBudgetPerUnit={maxBudgetPerUnit}
            avgBudgetPerUnit={avgBudgetPerUnit}
            includeSoftCost={includeSoftCost}
            typeOfReport={ReportTypes.CC}
            displayGraph={true}
            scatterRefGSF={scatterRefGSF}
            scatterRefUnit={scatterRefUnit}
            scatterRefNSF={scatterRefNSF}
          />
          <Grid size={{ xs: 12 }} p={1}>
            <CCReportBudgetPerDivision
              report={report}
              displayProjections={false}
              divisions={divisions}
              costPerDivision={costPerDivision}
              typeOfReport={ReportTypes.CC}
              includeSoftCost={includeSoftCost}
            />
          </Grid>
        </>
      )}
      {tabValue === 1 && (
        <>
          <Grid size={{ xs: 12 }} p={1}>
            <CCReportInputsCard report={report} updateReport={updateReport} />
          </Grid>
          <CCReportBudgetInfo
            report={report}
            displayProjections={true}
            maxBudgetPerGSF={maxBudgetPerGSF}
            avgBudgetPerGSF={avgBudgetPerGSF}
            minBudgetPerGSF={minBudgetPerGSF}
            minBudgetPerNSF={minBudgetPerNSF}
            maxBudgetPerNSF={maxBudgetPerNSF}
            avgBudgetPerNSF={avgBudgetPerNSF}
            minBudgetPerUnit={minBudgetPerUnit}
            maxBudgetPerUnit={maxBudgetPerUnit}
            avgBudgetPerUnit={avgBudgetPerUnit}
            includeSoftCost={includeSoftCost}
            typeOfReport={ReportTypes.CC}
          />
          <Grid size={{ xs: 12 }} p={1}>
            <CCReportBudgetPerDivision
              report={report}
              displayProjections={true}
              divisions={divisions}
              costPerDivision={costPerDivision}
              typeOfReport={ReportTypes.CC}
              includeSoftCost={includeSoftCost}
            />
          </Grid>
        </>
      )}
      <NotesAccordion
        entityId={report.id}
        notes={notes}
        setNotes={setNotes}
        apiCalls={{
          getNotes: getNotes,
          addOrUpdateNote: addOrUpdateNoteToReport,
          deleteNote: deleteNoteFromReport,
        }}
      />
      <br></br>
      <CCReportFooter
        report={report}
        isReportDirty={isReportDirty}
        isEditMode={isEditMode}
        isDeleting={isDeleting}
        isReportLoading={isReportLoading}
        anchorEl={anchorEl}
        open={open}
        joinDateAndUser={joinDateAndUser}
        handleDelete={handleDelete}
        saveReport={saveReport}
        handleCreateCopy={handleCreateCopy}
        handleClick={handleClick}
        handleClose={handleClose}
        handleOpenCompsDialog={handleOpenCompsDialog}
        handleExportAsWord={openExportDialog}
        onExportInvoice={handleExportInvoice}
        isExporting={isExporting}
        typeofReport={ReportTypes.CC}
      />
      <CCExportDialog
        open={exportReportDialogOpen}
        onClose={closeExportDialog}
        exportPreferences={exportPreferences}
        setExportPreferences={setExportPreferences}
        onExport={handleExport}
      />
      {openCopyReportDialog && (
        <GenericCopyReportDialog
          open={true}
          onClose={handleCloseDialog}
          defaultReportName={`${report.name} (Copy)`}
          handleCreateCopy={handleCreateCopyCC}
        />
      )}
      <ProjectCompsDialog
        open={isCompsDialogOpen}
        onClose={handleCloseCompsDialog}
        onSelect={addComps}
        isFromReport={true}
        disabledCompsIds={report.comparableProjects?.map(comp => comp.comp.id)}
        isAdding={isAddingComps}
        prevAppliedFilters={report.filtersString}
      />
    </>
  );
};

export default CCReportRenderer;
